import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { keys } from '@/lib/queryKeys';
import {
  fetchActiveConsentForms, fetchConsentHistory, fetchConsentRequirements, fetchConsentTemplates,
  publishConsentTemplate, recordConsent, recordConsents, updateConsentTemplate,
} from './api';
import type { ConsentAnswer, ConsentTypeId } from './types';

/**
 * The blank consent forms, for a patient who does not exist yet — the
 * Registration step. Separate from useConsentRequirements because there is no
 * :uhid to key on, not because the shape differs.
 */
export function useActiveConsentForms(registrationOnly = true) {
  return useQuery({
    queryKey: [...keys.config.consentTemplates(), 'active', { registrationOnly }],
    queryFn: () => fetchActiveConsentForms(registrationOnly),
    // Admin-published wording, changes rarely — same stale window as the other
    // admin-configured lookups (useIntakeConfig).
    staleTime: 5 * 60_000,
  });
}

/**
 * The consent picture for one patient: live wording, current answers, what is
 * due. Shared by the Registration consent step and the Patient Records consent
 * panel so the two can never disagree about whether a consent is on file.
 *
 * `staleTime: 0` on purpose, unlike the admin-config queries nearby — a consent
 * recorded at another desk seconds ago must not be missed by the gate that
 * decides whether Registration may complete.
 */
export function useConsentRequirements(uhid: string | null | undefined, registrationOnly = false) {
  return useQuery({
    queryKey: [...keys.patients.consent(uhid || ''), { registrationOnly }],
    queryFn: () => fetchConsentRequirements(uhid!, registrationOnly),
    enabled: !!uhid,
    staleTime: 0,
  });
}

export function useConsentHistory(uhid: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.patients.consentHistory(uhid || ''),
    queryFn: () => fetchConsentHistory(uhid!),
    enabled: !!uhid && enabled,
  });
}

/** Records the whole consent screen in one transaction. */
export function useRecordConsents(uhid: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ consents, visitId }: { consents: ConsentAnswer[]; visitId?: number | null }) =>
      recordConsents(uhid!, consents, visitId),
    onSuccess: () => {
      // Invalidate the patient's whole consent subtree — requirements (keyed
      // with a registrationOnly suffix) and history both hang off it.
      qc.invalidateQueries({ queryKey: keys.patients.consent(uhid || '') });
    },
  });
}

/** Re-records one consent (Patient Records: record, withdraw, renew after expiry). */
export function useRecordConsent(uhid: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ type, granted, notes }: { type: ConsentTypeId; granted: boolean; notes?: string }) =>
      recordConsent(uhid!, type, granted, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.patients.consent(uhid || '') });
    },
  });
}

// ── Admin ────────────────────────────────────────────────────────

export function useConsentTemplates() {
  return useQuery({
    queryKey: keys.config.consentTemplates(),
    queryFn: fetchConsentTemplates,
    staleTime: 5 * 60_000,
  });
}

export function usePublishConsentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: publishConsentTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.config.consentTemplates() }),
  });
}

export function useUpdateConsentTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: {
      id: number;
      validity_days?: number | null;
      is_active?: boolean;
      is_enabled?: boolean;
      is_mandatory?: boolean;
      label?: string;
      description?: string | null;
    }) => updateConsentTemplate(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.config.consentTemplates() }),
  });
}
